import { useState, useEffect } from 'react';
import {
  DAYS_TO_HOURS,
  HOURS_TO_DAYS,
  HOURS_TO_MINUTES,
  MILLISECONDS_TO_SECONDS,
  MINUTES_TO_HOURS,
  MINUTES_TO_SECONDS,
  SECONDS_TO_MINUTES,
  YEARS_TO_DAYS
} from '../constants';

export interface TimeLeft {
  secondsLeft: string;
  minutesLeft: string;
  hoursLeft: string;
  daysLeft: string;
  isOver: boolean;
  currentEndDate: number;
}

export const emptyDaysLeft = {
  secondsLeft: '0',
  minutesLeft: '0',
  hoursLeft: '0',
  daysLeft: '0',
  isOver: true
};

const addZero = (value: number): string => {
  const TEN = 10.0;
  return value < TEN ? `0${value.toString()}` : value.toString();
};

const formatTime = (time: number, hasZeroes: boolean): string =>
  hasZeroes ? addZero(time) : time.toString();

export const calculateTimeLeft = (endDate: number, hasZeroes = false): TimeLeft => {
  const distance = endDate - Date.now();

  const seconds = Math.floor(distance * MILLISECONDS_TO_SECONDS);
  const minutes = Math.floor(seconds * SECONDS_TO_MINUTES);
  const hours = Math.floor(minutes * MINUTES_TO_HOURS);
  const days = Math.floor(hours * HOURS_TO_DAYS);

  return {
    secondsLeft: formatTime(Math.floor(seconds % MINUTES_TO_SECONDS), hasZeroes),
    minutesLeft: formatTime(Math.floor(minutes % HOURS_TO_MINUTES), hasZeroes),
    hoursLeft: formatTime(Math.floor(hours % DAYS_TO_HOURS), hasZeroes),
    daysLeft: formatTime(days % YEARS_TO_DAYS, hasZeroes),
    isOver: Date.now() > endDate,
    currentEndDate: endDate
  };
};

export type UseTimer = [TimeLeft, React.Dispatch<React.SetStateAction<number>>];

export const useTimer = (countdownDate: number, hasZeroes = false): UseTimer => {
  const [endDate, setEndDate] = useState<number>(countdownDate);
  const [timeLeft, setTimeLeft] = useState<TimeLeft>(calculateTimeLeft(endDate, hasZeroes));

  useEffect(() => {
    const animationFrameId: number = requestAnimationFrame(() =>
      setTimeLeft(calculateTimeLeft(endDate, hasZeroes))
    );

    return (): void => cancelAnimationFrame(animationFrameId);
  }, [endDate, timeLeft, hasZeroes]);

  return [timeLeft, setEndDate];
};

export default useTimer;
